/**
 * SimulateInline (MASTER-PRD §6.5.7) — the embeddable doorway into the
 * Organizational Simulation harness. Where the full ScenarioPicker is the "lab"
 * (Operations ▸ Simulate), this is the *context-seeded* launcher you drop at a
 * decision point: a workflow, a forecast, a staffing surface. It pre-binds ONE
 * scenario + its scope from the surrounding context, runs it against the shared
 * engine (`/api/intel/simulate`, recorded to the audit chain), and renders the
 * projected delta inline. The org reasons where the decision is made — simulation
 * is a capability woven through the product, not a separate screen.
 *
 * Collapsed it's a single "Simulate" affordance; expanded it shows the one
 * relevant lever + the `ProjectionDeltaCard`. Self-contained (owns its run via
 * `apiCall`), so embedding is one line per surface.
 */

import * as React from "react";
import { FlaskConical } from "../icons";
import { Card } from "../card";
import { Button } from "../button";
import { Input } from "../input";
import { Label } from "../label";
import { Alert } from "../alert";
import { apiCall } from "../api/client";
import {
  intelSimulate,
  type SimulateScenarioId,
  type SimulateScenarioResult,
} from "../api/contracts/intel-simulate";
import { ProjectionDeltaCard } from "./projection-delta-card";

export interface SimulateInlineProps {
  /** The single scenario this doorway runs (inflow · hire · remove-kind). */
  scenario: SimulateScenarioId;
  /** Commitment-kind scope (inflow scope / remove-kind target). */
  kind?: string;
  /** The human "what if?" headline, phrased for THIS surface. */
  prompt: string;
  /** Label for the lever input (inflow → "Inflow change %", hire → "Hires"). */
  leverLabel?: string;
  /** Initial lever value (inflow deltaPct default 25; hire default 1). */
  defaultLever?: number;
  /** Render expanded immediately instead of behind a "Simulate" button. */
  defaultOpen?: boolean;
  className?: string;
}

export function SimulateInline({
  scenario,
  kind,
  prompt,
  leverLabel,
  defaultLever,
  defaultOpen = false,
  className,
}: SimulateInlineProps) {
  const hasLever = scenario !== "remove-kind";
  const [open, setOpen] = React.useState(defaultOpen);
  const [lever, setLever] = React.useState(
    defaultLever ?? (scenario === "inflow" ? 25 : 1),
  );
  const [result, setResult] = React.useState<SimulateScenarioResult | null>(null);
  const [running, setRunning] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const run = async () => {
    setRunning(true);
    setError(null);
    try {
      const body =
        scenario === "inflow"
          ? { scenario, kind, deltaPct: lever }
          : scenario === "hire"
            ? { scenario, hires: Math.max(0, Math.round(lever)) }
            : { scenario, kind };
      setResult(await apiCall(intelSimulate.run, { body }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Simulation failed");
    } finally {
      setRunning(false);
    }
  };

  if (!open) {
    return (
      <Button
        variant="outline"
        size="sm"
        className={className}
        onClick={() => setOpen(true)}
      >
        <FlaskConical strokeWidth={1.75} className="size-3.5" />
        Simulate
      </Button>
    );
  }

  const label =
    leverLabel ?? (scenario === "inflow" ? "Inflow change %" : "Hires");

  return (
    <Card className={className}>
      <div className="flex flex-col gap-4 px-6 py-5">
        <div className="flex items-start gap-2">
          <FlaskConical
            strokeWidth={1.75}
            className="mt-0.5 size-4 shrink-0 text-muted-foreground"
          />
          <div>
            <h3 className="type-title font-medium">{prompt}</h3>
            <p className="type-subtext text-muted-foreground">
              Forks the event stream, replays it under this hypothesis, reads the
              projected Outcomes — computed locally, recorded to the audit chain.
            </p>
          </div>
        </div>

        <div className="flex items-end gap-3">
          {hasLever ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sim-inline-lever">{label}</Label>
              <Input
                id="sim-inline-lever"
                type="number"
                className="w-28"
                value={lever}
                min={scenario === "hire" ? 0 : undefined}
                onChange={(e) => setLever(Number(e.target.value))}
              />
            </div>
          ) : null}
          <Button onClick={run} disabled={running}>
            {running ? "Running…" : "Run what-if"}
          </Button>
        </div>

        {error ? <Alert variant="destructive">{error}</Alert> : null}
        {result ? <ProjectionDeltaCard {...result} /> : null}
      </div>
    </Card>
  );
}
